import {
  budgetBand,
  expectedBy,
  monthBudget,
  kpiWord,
  settingFor,
  type BudgetBand,
  type BudgetMap,
  type KpiSettingsMap,
} from '../../audit/budgets';
import { budgetKeyFor, kpiNumber } from '../../audit/kpiContext';
import type { KpiAudit, KpiCell, KpiColumn } from '../../audit/kpis';
import { easternIsoDay } from '../../data/taskRules';
import type { StoreBudgetRow } from '../../data/storeBudgets';

/**
 * B8 colours for the KPI tables (owner rulings 2026-10-25): each visit's
 * value against its store's budget for that month (month-to-date counts
 * pro-rated to the visit's Eastern day), averaged per cell, then green /
 * amber / red by budgetBand. Pure helpers shared by Analytics → KPIs and
 * the store page's KPI table.
 */

/** The template's KPI settings — the newest version that has any (they're the same on every version). */
export function familyKpiSettings(rows: readonly { version: number; kpiSettings?: KpiSettingsMap }[]): KpiSettingsMap | null {
  const withSettings = rows.filter((r) => r.kpiSettings && Object.keys(r.kpiSettings.q).length > 0);
  if (withSettings.length === 0) return null;
  return [...withSettings].sort((a, b) => b.version - a.version)[0]!.kpiSettings!;
}

/** `expectedOf` for kpiTable: the visit's budget, by its store, year and month. */
export function visitExpected(
  rows: readonly StoreBudgetRow[],
  templateKey: string,
  settings: KpiSettingsMap | null,
  /** The family's budget / goal question ids (src/audit/kpis.ts budgetColumns); others are never coloured. */
  budgetIds?: ReadonlySet<string>,
): (a: KpiAudit, c: KpiColumn) => number | null {
  const byStoreYear = new Map<string, BudgetMap>();
  for (const r of rows) byStoreYear.set(`${r.storeId}:${r.year}`, r.budgets);
  return (a, c) => {
    if (budgetIds && !budgetIds.has(c.id)) return null;
    // The visit's day = when it started (the form compared on the day values were
    // typed); submitted is the fallback. QA 2026-10-25: an Oct 31 visit submitted
    // Nov 1 must not be judged against November's budget.
    const when = a.startedAt || a.submittedAt;
    if (!when || Number.isNaN(Date.parse(when))) return null;
    const day = easternIsoDay(new Date(when));
    const map = byStoreYear.get(`${a.storeId}:${Number(day.slice(0, 4))}`);
    const key = budgetKeyFor(map, templateKey);
    if (!map || key === null) return null;
    const setting = settingFor(settings, c.id, c.type);
    return expectedBy(monthBudget(map, key, c.id, Number(day.slice(5, 7)) - 1), setting, day);
  };
}

export interface CellBudget {
  band: BudgetBand;
  /** Tooltip / screen-reader text. */
  title: string;
}

/** The colour and wording for one KPI cell; null when no visit in it had a budget. */
export function cellBudget(cell: KpiCell | undefined, column: KpiColumn, settings: KpiSettingsMap | null): CellBudget | null {
  const b = cell?.budget;
  if (!b) return null;
  const setting = settingFor(settings, column.id, column.type);
  const band = budgetBand(b.avgValue, b.avgExpected, column.type, setting.dir);
  const unit = column.type === 'percent' ? '%' : '';
  const expected = `${kpiNumber(Math.round(b.avgExpected * 10) / 10)}${unit}`;
  const word = kpiWord(setting);
  const basis = setting.kind === 'mtd' ? `${word} (prorated to visit days)` : word;
  let how: string;
  if (column.type === 'percent') {
    const pts = Math.round((b.avgValue - b.avgExpected) * 10) / 10;
    how = pts === 0 ? `on ${word.toLowerCase()}` : `${kpiNumber(Math.abs(pts))} pts ${pts > 0 ? 'over' : 'under'}`;
  } else {
    how = b.avgExpected > 0 ? `${Math.round((b.avgValue / b.avgExpected) * 100)}% of ${word.toLowerCase()}` : `${word.toLowerCase()} 0`;
  }
  const dir = setting.dir === 'down' ? ' · lower is better' : '';
  const over = b.n === 1 ? '' : ` · ${b.n} with a ${word.toLowerCase()}`;
  return { band, title: `${basis}: ${expected} — ${how}${dir}${over}` };
}

/** Years a range [start, end) touches (Eastern), for reading budgets. */
export function yearsOf(startIso: string, endIso: string): number[] {
  const first = Number(easternIsoDay(new Date(startIso)).slice(0, 4));
  const last = Number(easternIsoDay(new Date(new Date(endIso).getTime() - 1)).slice(0, 4));
  const out: number[] = [];
  for (let y = first; y <= last && out.length < 5; y++) out.push(y);
  return out;
}
